import { useCallback, useEffect, useState } from "react";
import type { AddressVerifyIssue } from "../steps/PaymentShippingStep/utils/validation";

export type PixiPaymentMethod =
  | "credit_card"
  | "paypal"
  | "direct_debit"
  | "invoice";

/** Hardware shipping service the user picks on the Payment & Shipping step. */
export type ShippingService = "standard" | "express";

/** Billing/shipping address captured on the Payment & Shipping step (GRWBKK-1544). */
export interface PixiAddress {
  firstName: string;
  secondName: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  /** State/province code or free text; only collected for billing. */
  state: string;
  street: string;
  building: string;
  city: string;
  postalCode: string;
}

export const emptyPixiAddress = (): PixiAddress => ({
  firstName: "",
  secondName: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  state: "",
  street: "",
  building: "",
  city: "",
  postalCode: "",
});

export interface PixiWizardSelection {
  screenCount: number;
  // Plan & Billing step (step 2). Optional until the user reaches and makes a
  // selection on that step; defaults are applied at the step level, not here,
  // so the persisted default-state shape stays `{ screenCount }`.
  planId?: "core" | "pro";
  currency?: "USD" | "GBP" | "EUR" | "CAD";
  billingCycle?: "year" | "month";
  // Exclusive deal selected on the Plan & Billing step, and the shipping
  // country for the free-device deal.
  dealId?: "free-device" | "discount";
  shippingCountry?: string;
  // Payment & Shipping step (step 3). Addresses persist; the card itself never
  // touches this state (it stays inside Stripe Elements).
  paymentMethod?: PixiPaymentMethod;
  billingAddress?: PixiAddress;
  shippingAddress?: PixiAddress;
  useSameAddressForShipping?: boolean;
  /**
   * Billing-only fields collected after the address (GRWBKK-1704). Kept at the
   * top level rather than on {@link PixiAddress} because that shape is shared
   * with the shipping address, which has no PO/VAT. `poNumber` is optional and
   * applied to invoices; `vatNumber` is submitted to Chargebee, which validates
   * it against the billing country (the frontend never validates it).
   */
  poNumber?: string;
  vatNumber?: string;
  /**
   * Hardware shipping service chosen on the Payment & Shipping step. Defaults to
   * "standard" (the cheapest) at the step level; the Checkout step uses it to
   * resolve the shipping rate instead of auto-selecting.
   */
  shippingService?: ShippingService;
  /**
   * Whether the Payment & Shipping step currently satisfies its required
   * fields. Used by the wizard footer to gate the Continue button. Card
   * completeness is ephemeral (held by Stripe), so validity is reported up
   * from the step rather than derived from persisted data.
   */
  paymentShippingValid?: boolean;
  /**
   * Stripe paymentMethod id (pm_…) for the credit-card method. In-memory ONLY —
   * stripped from the localStorage write (see PERSIST_OMIT), so a refresh drops
   * it and the card is re-entered. Never store raw card data here.
   */
  cardPaymentMethodId?: string;
  /**
   * Whether the purchase has succeeded — flips the wizard to the Complete
   * (success) screen. In-memory ONLY (see PERSIST_OMIT): a refresh drops it so
   * the user returns to Checkout rather than a sticky success screen.
   */
  purchased?: boolean;
  /**
   * Per-field issues from the backend address verifier, written on Continue.
   * In-memory ONLY (see PERSIST_OMIT): a verification is only meaningful for the
   * address currently in the form, so a refresh drops it and Continue re-verifies.
   */
  addressVerifyIssues?: AddressVerifyIssue[];
}

const DEFAULT_STATE: PixiWizardSelection = {
  screenCount: 1,
};

const storageKeyFor = (orgId: string) => `pixi-wizard-state:${orgId}`;

/** Ephemeral fields held in memory but never written to localStorage. */
const PERSIST_OMIT: (keyof PixiWizardSelection)[] = [
  "cardPaymentMethodId",
  "purchased",
  // Validity is derived from the (already-omitted) card token and the live
  // Stripe fields, so a persisted `true` would always be a post-refresh lie —
  // the token is gone and the card fields re-mount empty. Drop it so validity
  // reads back `undefined` and the footer gate blocks Continue until the step
  // recomputes it.
  "paymentShippingValid",
  "addressVerifyIssues",
];

const toPersisted = (
  state: PixiWizardSelection,
): Partial<PixiWizardSelection> => {
  const copy: Partial<PixiWizardSelection> = { ...state };
  PERSIST_OMIT.forEach((key) => delete copy[key]);
  return copy;
};

const isValidScreenCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 1;

const validateState = (raw: unknown): PixiWizardSelection => {
  if (typeof raw !== "object" || raw === null) return { ...DEFAULT_STATE };
  const record = raw as Record<string, unknown>;
  // Cast is safe: spread preserves unknown future fields; screenCount is validated above.
  return {
    ...record,
    screenCount: isValidScreenCount(record.screenCount)
      ? record.screenCount
      : DEFAULT_STATE.screenCount,
  } as PixiWizardSelection;
};

const readSavedState = (orgId: string | undefined): PixiWizardSelection => {
  if (!orgId) return { ...DEFAULT_STATE };
  try {
    const raw = window.localStorage.getItem(storageKeyFor(orgId));
    if (!raw) return { ...DEFAULT_STATE };
    return validateState(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_STATE };
  }
};

export interface UsePixiWizardState {
  state: PixiWizardSelection;
  updateState: (patch: Partial<PixiWizardSelection>) => void;
}

export interface StepProps {
  state: PixiWizardSelection;
  updateState: (patch: Partial<PixiWizardSelection>) => void;
  /**
   * Jump to another step to edit it, returning to the current step afterwards.
   * Provided by the wizard; used by Checkout's review-card pencils.
   */
  goToStep?: (step: number) => void;
}

export const usePixiWizardState = (
  orgId: string | undefined,
): UsePixiWizardState => {
  const [state, setState] = useState<PixiWizardSelection>(() =>
    readSavedState(orgId),
  );

  useEffect(() => {
    if (!orgId) return;
    try {
      window.localStorage.setItem(
        storageKeyFor(orgId),
        JSON.stringify(toPersisted(state)),
      );
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore.
    }
  }, [orgId, state]);

  const updateState = useCallback((patch: Partial<PixiWizardSelection>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  return { state, updateState };
};
